import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Row,
  Col,
  InputNumber,
  Switch,
  Alert,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TransactionResponse } from '../../model/Transaction.model';
import TransactionService from '../../service/transaction-service/Transaction.service';

const { Option } = Select;

interface EditWithdrawalModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transaction: TransactionResponse;
}

const EditWithdrawalModal: React.FC<EditWithdrawalModalProps> = ({
  open,
  onClose,
  onSuccess,
  transaction,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Check if transaction is completed (read-only mode)
  const isCompleted = transaction.status === 'COMPLETED';

  useEffect(() => {
    if (open && transaction) {
      // Populate form with transaction data
      form.setFieldsValue({
        posDeviceId: transaction.posDevice?.id,
        transactionDate: transaction.transactionDate
          ? dayjs(transaction.transactionDate)
          : null,
        amount: transaction.amount,
        feeForCustomer: transaction.feeForCustomer,
        customerType: transaction.customerType || '%',
        feeApplied: transaction.feeApplied,
        commissionRateForPartner: transaction.commissionRateForPartner || 0,
        status: transaction.status,
        returnToCompany: transaction.returnToCompany,
      });
    }
  }, [open, transaction, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const requestData = {
        maturityScheduleId: 0, // Required for API but not used for withdrawal edits
        posDeviceId: values.posDeviceId,
        transactionDate: values.transactionDate.format('YYYY-MM-DD'),
        amount: values.amount,
        transactionType: 'withdrawal' as const,
        feeForCustomer: values.feeForCustomer || 0,
        customerType: values.customerType,
        feeApplied: values.feeApplied || 0,
        commissionRateForPartner: transaction.commissionRateForPartner || 0, // Keep original commission rate
        status: values.status,
        returnToCompany: transaction.returnToCompany, // Keep original returnToCompany
        transactionFor: 'withdrawal' as const,
      };

      // Get employee ID from localStorage or context
      const employeeData = localStorage.getItem('employee');
      const employeeId = employeeData ? JSON.parse(employeeData).id : 1;

      const response = await TransactionService.updateWithdrawalTransaction(
        transaction.id,
        requestData,
        employeeId
      );

      if (response.success) {
        message.success('Cập nhật giao dịch thành công');
        form.resetFields();
        onSuccess();
      } else {
        throw new Error(
          response.message || 'Có lỗi xảy ra khi cập nhật giao dịch'
        );
      }
    } catch (error: any) {
      console.error('Error updating withdrawal transaction:', error);
      message.error(error.message || 'Có lỗi xảy ra khi cập nhật giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <InfoCircleOutlined className="text-blue-500" />
          <span>
            {isCompleted ? 'Xem Chi Tiết Giao Dịch' : 'Sửa Giao Dịch Rút Tiền'}
          </span>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      {isCompleted && (
        <Alert
          message="Giao dịch đã hoàn thành"
          description="Giao dịch đã hoàn thành không thể chỉnh sửa. Bạn chỉ có thể xem thông tin."
          type="info"
          showIcon
          className="mb-4"
        />
      )}

      {!isCompleted && (
        <Alert
          message="Lưu ý khi sửa giao dịch rút tiền"
          description="Khi sửa số tiền, hệ thống sẽ tự động điều chỉnh số dư đã ứng trước. Ví dụ: Từ 15tr thành 18tr sẽ trừ thêm 3tr từ tài khoản."
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          customerType: '%',
          status: 'PENDING',
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="transactionDate"
              label="Ngày giao dịch"
              rules={[
                { required: true, message: 'Vui lòng chọn ngày giao dịch' },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày giao dịch"
                disabled={isCompleted}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="amount"
              label="Số tiền rút"
              rules={[
                { required: true, message: 'Vui lòng nhập số tiền' },
                { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Nhập số tiền"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                disabled={isCompleted}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="customerType" label="Loại phí khách hàng">
              <Select disabled={isCompleted}>
                <Option value="%">Phần trăm (%)</Option>
                <Option value="vn">Tiền mặt (VND)</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="feeForCustomer"
              label="Phí khách hàng"
              rules={[{ type: 'number', min: 0, message: 'Phí không được âm' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Nhập phí khách hàng"
                min={0}
                step={0.01}
                disabled={isCompleted}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="feeApplied"
              label="Phí POS (%)"
              rules={[{ type: 'number', min: 0, message: 'Phí không được âm' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Nhập phí POS"
                min={0}
                max={100}
                step={0.01}
                disabled={isCompleted}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="commissionRateForPartner" label="Phí CTV (%)">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Phí CTV từ thông tin khách hàng"
                min={0}
                max={100}
                step={0.01}
                readOnly
                className="bg-gray-50"
                disabled
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item name="status" label="Trạng thái">
              <Select disabled={isCompleted}>
                <Option value="PENDING">Đang chờ</Option>
                <Option value="COMPLETED">Hoàn thành</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <div className="text-sm text-gray-600 pt-6">
              <p>
                <strong>Luồng tiền:</strong>{' '}
                {transaction.returnToCompany ? (
                  <span className="text-blue-600">Về tài khoản công ty</span>
                ) : (
                  <span className="text-green-600">Về tài khoản nhân viên</span>
                )}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                (Không thể thay đổi luồng tiền khi sửa)
              </p>
            </div>
          </Col>
        </Row>

        <div className="border-t pt-4 mt-4">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Thẻ hiện tại:</strong> ***
                  {transaction.creditCard?.cardNumber?.slice(-4)} -{' '}
                  {transaction.creditCard?.cardHolderName}
                </p>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="text-sm text-gray-600">
                <p>
                  <strong>POS hiện tại:</strong>{' '}
                  {transaction.posDevice?.deviceCode} -{' '}
                  {transaction.posDevice?.location}
                </p>
              </div>
            </Col>
          </Row>
          <p className="text-xs text-gray-500 mt-2">
            <strong>Lưu ý:</strong> Thẻ tín dụng và thiết bị POS không thể thay
            đổi khi sửa giao dịch
          </p>
        </div>

        <Form.Item className="mb-0 mt-4">
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>
              {isCompleted ? 'Đóng' : 'Hủy'}
            </Button>
            {!isCompleted && (
              <Button type="primary" htmlType="submit" loading={loading}>
                Cập nhật giao dịch
              </Button>
            )}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditWithdrawalModal;
